import { Check, ChevronsUpDown, Loader2, User, X } from 'lucide-react';
import * as React from 'react';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

export interface ContactPickerProps {
  /** Selected contact id, or null when nothing is linked. */
  value: number | null;
  onChange: (contactId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/** One line of disambiguating context (company · primary email) under the contact's name. */
function contactSubtitle(contact: {
  companyName: string | null;
  emails?: { email: string; isPrimary: boolean | null }[];
}): string {
  const primaryEmail =
    contact.emails?.find((e) => e.isPrimary)?.email ?? contact.emails?.[0]?.email;
  return [contact.companyName, primaryEmail].filter(Boolean).join(' · ');
}

/**
 * Searchable single-contact combobox backed by the FTS-powered `contacts.search`
 * endpoint — built for workspaces with thousands of similarly-named contacts, so
 * each result shows company / primary email / type to tell near-duplicates apart.
 * Presentation is client-agnostic apart from the shared `trpc` client, matching
 * `LinkedContactFilter`; prefer this whenever a task/thread/etc. needs to pick one
 * contact rather than re-implementing a search box.
 */
export function ContactPicker({
  value,
  onChange,
  placeholder = 'Search contacts…',
  disabled = false,
  className,
  id,
}: ContactPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  React.useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timeout);
  }, [search]);

  const searchQuery = trpc.contacts.search.useQuery(
    { query: debouncedSearch || undefined, sortBy: 'displayName', sortDir: 'asc', limit: 20 },
    { enabled: open, placeholderData: (prev) => prev }
  );

  // Resolve the trigger label for the current selection. `getById` shares its cache
  // with the rest of the app, so this is usually an instant read.
  const selectedQuery = trpc.contacts.getById.useQuery({ id: value! }, { enabled: value != null });
  const selectedLabel =
    value == null
      ? undefined
      : selectedQuery.data?.displayName || selectedQuery.data?.label || 'Selected contact';

  const results = searchQuery.data?.items ?? [];

  const handleSelect = (contactId: number) => {
    onChange(contactId === value ? null : contactId);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            value == null && 'text-muted-foreground',
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <User className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate">{selectedLabel ?? placeholder}</span>
          </span>
          {value != null ? (
            <X
              className="h-3.5 w-3.5 shrink-0 opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }}
            />
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        {/* Server-side FTS drives the results, so disable cmdk's built-in filtering. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, company, email…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {searchQuery.isFetching && results.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            ) : (
              <CommandEmpty>No contacts found.</CommandEmpty>
            )}
            <CommandGroup>
              {results.map((contact) => {
                const subtitle = contactSubtitle(contact);
                return (
                  <CommandItem
                    key={contact.id}
                    value={String(contact.id)}
                    onSelect={() => handleSelect(contact.id)}
                    className="flex items-start gap-2"
                  >
                    <Check
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        value === contact.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">
                        {contact.displayName || contact.label}
                      </span>
                      {subtitle && (
                        <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
                      )}
                    </div>
                    {contact.contactType?.label && (
                      <span className="ml-auto shrink-0 self-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {contact.contactType.label}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
